import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, Loader2 } from "lucide-react";
import { searchProducts, resolveImg } from "../services/api";

const SearchBar = ({ variant = "desktop", onNavigate }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(() => {
      searchProducts(q.trim())
        .then((data) => { setResults(data.slice(0, 6)); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    onNavigate && onNavigate();
    navigate(path);
  };

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) go(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div ref={boxRef} className={`relative ${variant === "desktop" ? "w-64" : "w-full"}`}>
      <form onSubmit={submit}>
        <div className="relative">
          <SearchIcon className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results.length && setOpen(true)}
            placeholder="Search products..."
            className="w-full h-10 pl-9 pr-9 rounded-full border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-[#C9A227] focus:bg-white transition-colors"
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-2xl border border-neutral-100 overflow-hidden z-50 animate-fade-up">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-[#C9A227] animate-spin" /></div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-neutral-400">No products found</div>
          ) : (
            <>
              {results.map((p) => (
                <button key={p.id} onClick={() => go(`/product/${p.id}`)} className="w-full flex items-center gap-3 p-2.5 hover:bg-neutral-50 transition-colors text-left">
                  <img src={resolveImg(p.image)} alt={p.name} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#14171c] truncate">{p.name}</div>
                    <div className="text-xs text-neutral-500">{p.subcategory}</div>
                  </div>
                </button>
              ))}
              <button onClick={() => go(`/search?q=${encodeURIComponent(q.trim())}`)} className="w-full py-2.5 text-sm font-semibold text-[#C9A227] hover:bg-neutral-50 border-t border-neutral-100">
                View all results
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
